/**
 * DigaVouch backend API client.
 *
 * Base URL is read from VITE_API_BASE_URL via @/lib/env.
 *
 * Auth (two distinct callers share this client):
 *   - Operator dashboard: the logged-in operator's Supabase access token is
 *     sent as `Authorization: Bearer <jwt>`. The backend resolves the org from
 *     the operator's `operator_memberships` row, so the dashboard is scoped to
 *     the logged-in user's org — NOT a build-time API key. (This is why the
 *     same FE build can serve multiple tenant subdomains safely.)
 *   - Applicant / voucher magic-link flows: no Supabase session; these routes
 *     authenticate by the session token in the URL path / JSON body, so no
 *     auth header is attached.
 *
 * Endpoint conventions (per backend mapping table 2026-04-19):
 *   - Status / consent take the session token in the URL path
 *   - All verify/* endpoints take session_token in the JSON body
 */

import { API_BASE_URL } from "@/lib/env";
import { getActiveSession, signOutEverywhere } from "@/integrations/supabase/authProviders";
import { ORG_SELECTION_HEADER, clearStaffOrgSelection, getStaffOrgSelection } from "@/lib/staffOrgSelection";
import type {
  SignalSummary as ContractSignalSummary,
  ConnectedProvider as ContractConnectedProvider,
  SessionStatusResponse,
  WorkflowStep as ContractWorkflowStep,
  VouchOutcomeResponse,
  CaseReviewSource as ContractCaseReviewSource,
  CaseReviewEvidenceItem as ContractCaseReviewEvidenceItem,
  CaseReviewTimelineEvent as ContractCaseReviewTimelineEvent,
  CaseReviewContinuityItem as ContractCaseReviewContinuityItem,
  CaseReviewPillar as ContractCaseReviewPillar,
  CaseReviewResponse,
} from "dv-api-contract";
import {
  parseApplicantIdvState,
  type ApplicantIdvState,
} from "@/lib/applicantIdvState";
import { parseRequiredIdv, type RequiredIdv } from "@/lib/requiredIdv";

const API_BASE = API_BASE_URL;


// ---------- UI-facing types (what components consume) ----------

export type SessionStatusName =
  | "pending_consent"
  | "in_progress"
  | "step_up_required"
  | "awaiting_vouches"
  | "completed"
  | "expired"
  | "declined";

// Wire types derived from the shared contract (dv-api-contract). These are the
// backend response shapes; the FE consumes them via z.infer so a backend
// rename/retype fails `tsc` here instead of drifting silently. See A-5.
export type SignalSummary = ContractSignalSummary;

/**
 * `email_proven` (platform#712 part 1, verified as of 2026-09-29, platform PR
 * jgalvarez/digavouch-platform#721, `routes/sessions.ts` connected_providers):
 * the server's answer to "did this connection prove the applicant controls an
 * email?". Optional on purpose: a backend from before that PR sends no such
 * field, and `src/lib/emailProof.ts` handles that case. Not yet in
 * dv-api-contract; move it there when the contract next changes.
 */
export type ConnectedProviderApi = ContractConnectedProvider & { email_proven?: boolean };

/**
 * One entry of a workflow definition's ordered step list (ADR-014).
 *
 * Re-exported from the contract rather than redeclared, so a backend retype
 * fails `tsc` here like every other wire shape. It was local only while the
 * pin sat at v0.1.3; `workflowStepSchema` landed in v0.1.4 and the pin now
 * reads v0.1.6.
 *
 * Kept as a named re-export because `flowPlan.ts` and its tests import it
 * from here — api.ts stays the one place the FE names a wire type.
 *
 * `satisfied_by` is carried but unused by the FE today — the definition's
 * alternatives are enforced by the backend gate, and the provider menus are
 * already narrower than what a definition can name.
 */
export type WorkflowStep = ContractWorkflowStep;

/** `progress` from GET /sessions/:token — the server's own view of how far along the applicant is. */
export interface WorkflowProgress {
  current_step?: number;
  steps_completed?: string[];
  steps_total?: number;
  percent_complete?: number;
}

export interface SessionStatus {
  session_id: string;
  org_name: string;
  /**
   * The org's vertical (`residential_leasing` / `hr_recruiting`), for the two
   * PUBLIC journeys. Both read this one endpoint — `voucherApi.activate`
   * returns a session token that resolves against `/sessions/:token/status`
   * too — so one field here serves the applicant flow and the voucher flow.
   *
   * Optional and open-ended on purpose: the backend stores it without a CHECK
   * constraint so a new kind of org costs no migration, which means an
   * unrecognised value must fall back rather than fail. `verticalFrom()` in
   * lib/verticalCopy.ts is the only thing that should read this directly.
   */
  org_industry?: string;
  applicant_name?: string;
  /**
   * Email captured on the initial application form. Used by the applicant
   * flow's email step to recommend an OAuth provider (Gmail/Outlook) or
   * pre-fill the OTP path. See `src/lib/emailRouting.ts` and CLAUDE.md.
   * Returned only to the holder of the magic-link token.
   */
  applicant_email?: string;
  status: SessionStatusName;
  /** 1-indexed step matching the 6-step UI stepper. */
  current_step: number;
  percent_complete: number;
  signals_summary: SignalSummary[];
  connected_providers: ConnectedProviderApi[];
  /**
   * OAuth providers this environment can actually complete a flow for —
   * the backend reports which credentials are configured
   * (BACKEND_INTEGRATION.md §26).
   *
   * Render a provider tile ONLY when its key is in here. Do not mirror backend
   * credentials in a VITE_* flag: the two drift, and an enabled tile over an
   * unconfigured backend dead-ends the applicant on "not configured".
   *
   * Normalized to `[]` when the backend doesn't send it (older deploy), which
   * means "advertise nothing" — the conservative direction. Providers that
   * predate this field keep their own rendering rules, so an old backend
   * doesn't blank the flow.
   */
  available_oauth_providers: string[];
  step_up_required?: boolean;
  expires_at?: string;
  /** ISO-8601 timestamp when the applicant submitted the application. */
  submitted_at?: string;
  /** True when this session belongs to a voucher (not the applicant). */
  is_voucher?: boolean;
  /** Present on voucher sessions — who they're vouching for. */
  vouching_for?: { applicant_name: string; relationship: string } | null;
  /** How many voucher invitations are in-flight (pending or accepted) for this inquiry. */
  pending_vouchers_count?: number;
  /** Backend step keys completed so far (e.g. ['consent', 'oauth_linkedin']). Used by VoucherFlow to resume the magic-link in the right place. */
  steps_completed?: string[];
  /** Per-org policy: does this voucher's tenant require Plaid IDV before submission? */
  voucher_idv_required?: boolean;
  /** Set true after the voucher exhausted IDV retries + called /verify/plaid-idv/skip. Backend gate honors this as a graceful bypass. */
  voucher_idv_skipped?: boolean;
  /** True when the voucher ALREADY has a verified Plaid IDV on the backend (data-source or document path). Lets a reopened session recognize prior IDV instead of re-gating step 1. */
  voucher_idv_verified?: boolean;
  /**
   * Per-org policy: does this tenant ASK the applicant for Plaid IDV?
   * (ADR-012 R3 — asked for, never enforced. Nothing is gated on it.)
   * Absent/false for every org today; BF-B3 turns it on for Braden Fellman.
   */
  applicant_idv_required?: boolean;
  /** Set true after the applicant declined IDV and the FE called /verify/plaid-idv/skip. Recorded and shown to the operator; it blocks nothing. */
  applicant_idv_skipped?: boolean;
  /** True when the applicant ALREADY has a verified Plaid IDV on the backend. Drives the ADR-012 R2 vouch-tier credit. */
  applicant_idv_verified?: boolean;
  /**
   * #525 — the backend's own computed answer to "has this applicant earned
   * the ADR-012 R2 vouch-tier credit", read by `hasEarnedIdvCredit()` instead
   * of deriving one from `applicant_idv_required && applicant_idv_verified`.
   * That derivation could disagree with the backend's real submit gate:
   * `applicant_idv_verified` comes from a cross-DB attestation lookup that
   * can under-report relative to `idv_captures.passed`, which is what this
   * field is sourced from. Absent (older backend) reads as false, same as
   * every other `applicant_idv_*` field.
   */
  idv_credit_earned?: boolean;
  /**
   * The vouch requirement the server's submit gate applies, and how far the
   * applicant is toward it. `required_by` names the rule that produced
   * `required`: `"flow"` when the session's workflow states its own number,
   * `"account_tier"` when it follows connected accounts. Read through
   * `flowSubmissionRule()` in `@/lib/applicantRequirements`, never directly.
   * Absent on an older backend, which reads as the account tier.
   */
  vouch_summary?: {
    sent: number;
    completed: number;
    required: number | null;
    required_by?: "flow" | "account_tier";
    /**
     * The invites the screen shows (with 0 pieces, where `required` is null,
     * what the first piece would ask for) and the pieces of evidence the gate
     * counts. Read through `serverVouchNumbers()`; absent on an older backend.
     */
    owed?: number;
    evidence_pieces?: number;
  };
  /** Whether the server's submit gate would accept this session right now. */
  can_submit?: boolean;
  /**
   * ADR-014 — which workflow definition this session was started under.
   *
   * NULL for every session predating the column, which is all leasing traffic.
   * The literal `'standard'` also means "no workflow" and must be treated as
   * null: `POST /inquiries` takes this from the request body unvalidated, and
   * the back end applies the same `key && key !== 'standard'` guard everywhere.
   *
   * Merged in from GET /sessions/:token, not /status — see `fetchSessionDetail`.
   */
  workflow_key?: string | null;
  /**
   * The definition's ORDERED step list. Order is significant and is
   * configuration: moving IDV to the end of a flow is a new definition version,
   * never a front-end change.
   *
   * Absent whenever `workflow_key` is. Not derivable from the org-level
   * `verification_steps` — a workflow may include steps that list omits, which
   * is exactly the IDW `tech_role` / `plaid_idv` case.
   */
  workflow_steps?: WorkflowStep[] | null;
  /**
   * The server's progress numbers for this session.
   *
   * ⚠️ `steps_total` counts the DEFINITION's steps. It is not the applicant
   * flow's screen count, because the vouch-invite and review screens are not
   * workflow steps. See `planTotalLabel` in `@/lib/flowPlan`.
   */
  workflow_progress?: WorkflowProgress | null;
  /** Raw backend payload, exposed for debugging / forward-compat. */
  _raw?: BackendSessionStatus;
}

// ---------- Raw backend payload (current GCP shape) ----------

// The GET /sessions/:token/status wire shape, owned by the contract.
export type BackendSessionStatus = SessionStatusResponse;

// ---------- Other DTOs ----------

export interface OAuthRedirectResponse {
  redirect_url: string;
}

export interface ApiError extends Error {
  status?: number;
  /** Backend error code, e.g. "INVALID_AUTH", "SESSION_EXPIRED". */
  code?: string;
  payload?: unknown;
}

// ---------- Normalizer ----------

const STATUS_MAP: Record<string, SessionStatusName> = {
  pending: "pending_consent",
  pending_consent: "pending_consent",
  in_progress: "in_progress",
  step_up_required: "step_up_required",
  awaiting_vouches: "awaiting_vouches",
  completed: "completed",
  expired: "expired",
  declined: "declined",
};

/** Exported for tests. */
export function normalizeSession(raw: BackendSessionStatus): SessionStatus {
  // Map backend `signals_verified: string[]` → UI signal summary entries.
  const signals: SignalSummary[] =
    raw.signals_summary ??
    (raw.signals_verified ?? []).map((key) => ({
      key,
      label: humanizeSignalKey(key),
      status: "verified" as const,
    }));

  const status = STATUS_MAP[raw.status] ?? "in_progress";
  // When the session is still awaiting consent, force step 1 (Consent) as
  // the active step regardless of what the backend reports.
  const current_step =
    status === "pending_consent" ? 1 : (raw.current_step ?? 1);

  return {
    session_id: raw.session_id,
    org_name: raw.org_name ?? "Your application",
    org_industry: raw.org_industry,
    applicant_name: raw.applicant_name,
    // Contract types applicant_email as nullable; the normalized UI type
    // treats absent and null identically (see classifyEmail fallback).
    applicant_email: raw.applicant_email ?? undefined,
    status,
    current_step,
    percent_complete:
      typeof raw.percent_complete === "number" ? raw.percent_complete : 0,
    signals_summary: signals,
    connected_providers: raw.connected_providers ?? [],
    // Absent → empty, i.e. "the backend advertises nothing". Gated tiles stay
    // hidden rather than appearing over a backend that can't serve them.
    //
    // Read off the contract directly now that the pin carries this field
    // (v0.1.6; BACKEND_INTEGRATION.md §26 used to be newer than the schema
    // package). The runtime filter stays regardless of the type: this is the
    // one field whose element type the FE cannot take on trust, because
    // normalizeSession does not validate — anything read off `raw` still has
    // to defend itself.
    available_oauth_providers: Array.isArray(raw.available_oauth_providers)
      ? raw.available_oauth_providers.filter((p): p is string => typeof p === "string")
      : [],
    step_up_required: raw.step_up_required,
    expires_at: raw.expires_at,
    submitted_at: raw.submitted_at ?? raw.completed_at,
    is_voucher: raw.is_voucher,
    vouching_for: raw.vouching_for ?? null,
    pending_vouchers_count: raw.pending_vouchers_count,
    voucher_idv_required: raw.voucher_idv_required,
    voucher_idv_skipped: raw.voucher_idv_skipped,
    voucher_idv_verified: raw.voucher_idv_verified,
    // Applicant-side IDV (BF-B1 / ADR-012). Read directly, exactly like the
    // voucher_idv_* siblings above, now that dv-api-contract v0.1.3 carries
    // them. The widening cast this replaced existed only because the schema
    // lagged the backend. Absent on any build older than BF-B1 → undefined,
    // which every consumer treats as false.
    applicant_idv_required: raw.applicant_idv_required,
    applicant_idv_skipped: raw.applicant_idv_skipped,
    applicant_idv_verified: raw.applicant_idv_verified,
    // #525 — not in the dv-api-contract pin yet (lands in v0.1.8; see
    // jgalvarez/dv-api-contract#4). Read via the same narrow widening cast
    // applicant_idv_* used before v0.1.3 caught up (comment above). Retire the
    // cast once the pin carries the field and read it off `raw` directly.
    idv_credit_earned: (raw as { idv_credit_earned?: boolean }).idv_credit_earned,
    // Not in the dv-api-contract pin yet (declared in v0.1.9). Same narrow
    // widening cast as `idv_credit_earned` above; read off `raw` directly once
    // the pin carries them.
    vouch_summary: (raw as { vouch_summary?: SessionStatus["vouch_summary"] }).vouch_summary,
    can_submit: (raw as { can_submit?: boolean }).can_submit,
    steps_completed: raw.steps_completed,
    _raw: raw,
  };
}

const SIGNAL_LABELS: Record<string, string> = {
  "contact.email": "Email contact",
  "contact.phone": "Phone contact",
  "digital.email_history": "Email account history",
  "digital.social_account": "Social account (LinkedIn)",
  "document.authenticity": "ID document authenticity",
  "human.vouch": "Human Vouch",
};

function humanizeSignalKey(key: string): string {
  if (SIGNAL_LABELS[key]) return SIGNAL_LABELS[key];
  return key
    .replace(/^[a-z]+\./, "")
    .replace(/[_.-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------- HTTP plumbing ----------

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (!API_BASE) {
    const err: ApiError = new Error(
      "VITE_API_URL is not configured. Set it to the backend base URL.",
    );
    err.status = 0;
    throw err;
  }

  // Operator auth: attach the Supabase access token when the operator is
  // logged in. getSession() works outside React and refreshes the token if
  // needed. Applicant/voucher flows have no session → accessToken is undefined
  // → no auth header (those routes authenticate by their session token).
  const session = await getActiveSession();
  const accessToken = session?.access_token;
  // Staff org switch: the selected org travels on EVERY operator call, and the
  // backend validates it every time. Operator calls only — an applicant or
  // voucher has no session and no selection to send.
  const staffOrg = accessToken ? getStaffOrgSelection() : null;

  const hasBody = init.body != null;
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      // Only send Content-Type when there is a body — some backends (Fastify)
      // reject body-less requests that advertise application/json.
      ...(hasBody ? { "Content-Type": "application/json" } : {}),
      Accept: "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(staffOrg ? { [ORG_SELECTION_HEADER]: staffOrg } : {}),
      ...(init.headers ?? {}),
    },
  });

  // Operator token rejected (expired / revoked) → bounce to login. Guarded to
  // `accessToken` so a bad APPLICANT session token (also a 401) falls through
  // to the flow's own error UI instead of redirecting the applicant to /login.
  if (res.status === 401 && accessToken && typeof window !== "undefined") {
    await signOutEverywhere();
    if (!window.location.pathname.startsWith("/login")) {
      window.location.assign("/login");
    }
  }

  const text = await res.text();
  const payload = text ? safeJson(text) : null;

  // The server refused the selected org (the grant was revoked, or the org
  // no longer allows staff access). It never falls back to the person's own
  // org on its own, so neither does this: drop the selection and start over
  // from the landing page, which resolves the home org afresh.
  if (res.status === 403 && staffOrg && typeof window !== "undefined") {
    const p = payload as { code?: string } | null;
    if (p?.code === "ORG_SELECTION_FORBIDDEN") {
      clearStaffOrgSelection();
      window.location.assign("/");
    }
  }

  if (!res.ok) throw apiErrorFrom(res.status, payload);

  return payload as T;
}

/** The error a non-2xx answer becomes: its status, the backend's `code`, and the body. */
export function apiErrorFrom(status: number, payload: unknown): ApiError {
  const p = (typeof payload === "object" ? payload : null) as
    | { message?: string; error?: string; code?: string }
    | null;
  const err: ApiError = new Error(p?.message ?? p?.error ?? `Request failed (${status})`);
  err.status = status;
  err.code = p?.code;
  err.payload = payload;
  return err;
}

export function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

const enc = encodeURIComponent;

/**
 * Shape of both `/verify/{email,phone}/confirm-code` responses. A WRONG code
 * is not an HTTP error — it is `200 {verified: false}`; only lockout (429),
 * expiry (410), a consumed/absent row (404), and replay (409) raise 4xx.
 *
 * These endpoints must NEVER go through `sessionRequest`: `normalizeSession`
 * coerces any body into a plausible-looking `SessionStatus` (an absent
 * `status` becomes `"in_progress"`), which silently discards `verified` and
 * makes every wrong code read as a success. That shipped on the email path
 * until J5 (2026-07-09), and was armed on the phone path behind dead UI.
 */
export interface VerifyCodeResponse {
  verified: boolean;
  signal_id?: string;
  line_type?: string;
}

/**
 * For endpoints that genuinely return a session status. `normalizeSession`
 * does not validate — it will happily shape a foreign body into a
 * `SessionStatus`, so only point this at routes that return one. (Runtime
 * validation against the dv-api-contract Zod schemas at the `request`
 * boundary would remove the footgun; tracked as a follow-up.)
 */
async function sessionRequest(
  path: string,
  init?: RequestInit,
): Promise<SessionStatus> {
  const raw = await request<BackendSessionStatus>(path, init);
  return normalizeSession(raw);
}

/**
 * The richer GET /api/v1/sessions/:token endpoint includes
 * `inquiry_details.metadata.name`, which the lighter /status endpoint omits,
 * and — since ADR-014 — the session's `workflow_key`, the definition's ordered
 * `steps`, and `progress`. We fetch it opportunistically alongside /status.
 */
interface BackendSessionDetails {
  inquiry_details?: {
    metadata?: { name?: string } | null;
  } | null;
  workflow_key?: unknown;
  steps?: unknown;
  progress?: unknown;
}

interface SessionDetailExtras {
  applicant_name?: string;
  workflow_key: string | null;
  workflow_steps: WorkflowStep[] | null;
  workflow_progress: WorkflowProgress | null;
}

/** Everything a failed or absent detail fetch degrades to: the legacy flow. */
const NO_DETAIL_EXTRAS: SessionDetailExtras = {
  applicant_name: undefined,
  workflow_key: null,
  workflow_steps: null,
  workflow_progress: null,
};

/**
 * Narrow, defensive read of the definition's step list.
 *
 * `request` does not validate, and this drives which screens the applicant is
 * shown — so a malformed entry drops out rather than reaching the renderer.
 * Returning null (not `[]`) for anything unusable is what makes the caller fall
 * back to the hardcoded flow instead of rendering an empty one.
 */
function parseWorkflowSteps(raw: unknown): WorkflowStep[] | null {
  if (!Array.isArray(raw)) return null;
  const steps: WorkflowStep[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const { step_type, required, satisfied_by } = entry as Record<string, unknown>;
    if (typeof step_type !== "string" || step_type.length === 0) continue;
    steps.push({
      step_type,
      // Absent `required` means required. A step the server forgot to flag is
      // safer shown as mandatory than quietly presented as skippable.
      required: required !== false,
      satisfied_by: Array.isArray(satisfied_by)
        ? satisfied_by.filter((s): s is string => typeof s === "string")
        : undefined,
    });
  }
  return steps.length > 0 ? steps : null;
}

function parseWorkflowProgress(raw: unknown): WorkflowProgress | null {
  if (!raw || typeof raw !== "object") return null;
  const { current_step, steps_completed, steps_total, percent_complete } =
    raw as Record<string, unknown>;
  return {
    current_step: typeof current_step === "number" ? current_step : undefined,
    steps_completed: Array.isArray(steps_completed)
      ? steps_completed.filter((s): s is string => typeof s === "string")
      : undefined,
    steps_total: typeof steps_total === "number" ? steps_total : undefined,
    percent_complete:
      typeof percent_complete === "number" ? percent_complete : undefined,
  };
}

async function fetchSessionDetail(token: string): Promise<SessionDetailExtras> {
  try {
    const details = await request<BackendSessionDetails>(
      `/api/v1/sessions/${enc(token)}`,
    );
    return {
      applicant_name: details?.inquiry_details?.metadata?.name ?? undefined,
      workflow_key:
        typeof details?.workflow_key === "string" ? details.workflow_key : null,
      workflow_steps: parseWorkflowSteps(details?.steps),
      workflow_progress: parseWorkflowProgress(details?.progress),
    };
  } catch {
    // Swallowed exactly as before. This call has always been best-effort — it
    // rides alongside /status, which is the request that actually decides
    // whether the page can render. A failure here costs the display name and,
    // now, the definition ordering; both degrade to the legacy flow.
    return NO_DETAIL_EXTRAS;
  }
}

// ---------- Public API ----------

export const applicantApi = {
  // Session — token in path. Merges applicant_name from the richer
  // /sessions/:token endpoint when /status doesn't include it.
  getSessionStatus: async (token: string): Promise<SessionStatus> => {
    const [status, detail] = await Promise.all([
      sessionRequest(`/api/v1/sessions/${enc(token)}/status`),
      fetchSessionDetail(token),
    ]);
    return {
      ...status,
      applicant_name: status.applicant_name ?? detail.applicant_name,
      workflow_key: detail.workflow_key,
      workflow_steps: detail.workflow_steps,
      workflow_progress: detail.workflow_progress,
    };
  },

  // Step 1 — consent (token in path)
  giveConsent: (token: string) =>
    sessionRequest(`/api/v1/sessions/${enc(token)}/consent`, {
      method: "POST",
      body: JSON.stringify({}),
    }),

  // Step 1 (alt) — applicant declines verification.
  // Terminal: backend sets session.status='declined' + inquiry.status=
  // 'declined_by_applicant' and expires the session. Idempotent — replaying
  // returns { already_declined: true }. Optional reason capped at 500 chars
  // server-side; we don't bother validating client-side.
  declineConsent: (token: string, reason?: string) =>
    request<{
      session_id: string;
      status: "declined";
      inquiry_id: string;
      already_declined: boolean;
    }>(`/api/v1/sessions/${enc(token)}/decline`, {
      method: "POST",
      body: JSON.stringify(reason ? { reason } : {}),
    }),

  // Step 2/3 — OAuth (session_token in body)
  startOAuth: (token: string, provider: "google" | "microsoft" | "linkedin" | "amazon" | "uber" | "apple" | "facebook" | "github") =>
    request<OAuthRedirectResponse>(`/api/v1/verify/oauth/${provider}`, {
      method: "POST",
      body: JSON.stringify({ session_token: token }),
    }),

  // Step 2 — email-code fallback.
  // Backend returns `email_id` (UUID) which must be echoed back in confirm.
  // In staging mode it also returns the generated `code` for testing since
  // no real email is sent.
  sendEmailCode: (token: string, email: string) =>
    request<{ ok: true; email_id: string; code?: string }>(
      `/api/v1/verify/email/send-code`,
      {
        method: "POST",
        body: JSON.stringify({ session_token: token, email }),
      },
    ),

  // Confirm an email OTP. Pass `email` so the backend can look up the latest
  // pending verification row for this (session, email) — letting the
  // applicant complete on a different device than the one that sent the
  // code. The backend still accepts the legacy `email_id` field if any
  // caller passes one, but we don't bother sending it any more.
  //
  // A WRONG code is not an HTTP error: the backend answers 200 with
  // `{verified: false}` (verified as of 2026-07-09,
  // `email-verification.ts:319-322`; only lockout/expiry/replay raise 4xx).
  // Callers MUST check `verified` — this used to run through `sessionRequest`,
  // whose SessionStatus normalization discarded the flag, so every wrong code
  // read as a success.
  confirmEmailCode: (token: string, email: string, code: string) =>
    request<VerifyCodeResponse>(`/api/v1/verify/email/confirm-code`, {
      method: "POST",
      body: JSON.stringify({ session_token: token, email, code }),
    }),

  // Step 6 — final submission. POST /api/v1/sessions/:token/submit (no body) →
  // normalized SessionStatus with status "completed". Shipped 2026-06-03;
  // callers must surface failures rather than assuming success (a 409 means the
  // server rejected the submit as not-ready / already-consented).
  submitSession: (token: string) =>
    sessionRequest(`/api/v1/sessions/${enc(token)}/submit`, {
      method: "POST",
      body: JSON.stringify({}),
    }),

  // Step 6 — phone step-up.
  //
  // NOTE: no UI reaches these today — `PhoneStepUp` has been unmounted dead
  // code since 381d68d (see CLAUDE.md §Step-up surfaces, finding a). Both
  // calls were also mis-shaped: the wire field names were `phone` where the
  // backend reads `phone_number` / `phone_id` (so every call 400'd), and
  // confirm ran through `sessionRequest`, discarding `verified` exactly as
  // the email path did. Corrected here against the live contract (verified as
  // of 2026-07-09, `verification.ts:315-398`) so re-homing the surface starts
  // from a working client.
  sendPhoneCode: (token: string, phoneNumber: string) =>
    request<{ phone_id: string; line_type?: string; code?: string }>(
      `/api/v1/verify/phone/send-code`,
      {
        method: "POST",
        body: JSON.stringify({ session_token: token, phone_number: phoneNumber }),
      },
    ),

  /** Like the email confirm: a wrong code is `200 {verified:false}`, never a
   *  4xx (`phone-verification.ts:250-252`). Callers MUST check `verified`. */
  confirmPhoneCode: (token: string, phoneId: string, code: string) =>
    request<VerifyCodeResponse>(`/api/v1/verify/phone/confirm-code`, {
      method: "POST",
      body: JSON.stringify({ session_token: token, phone_id: phoneId, code }),
    }),

  // Step 5 — voucher invitations
  inviteVoucher: (
    token: string,
    payload: { name: string; email: string; relationship: string },
  ) =>
    request<{
      invitation_id: string;
      status: "pending";
      expires_at: string;
      voucher_link: string;
    }>(`/api/v1/sessions/${enc(token)}/voucher-invitations`, {
      method: "POST",
      body: JSON.stringify({ ...payload, channel: "email" }),
    }),

  listVoucherInvitations: (token: string) =>
    request<{ invitations: VoucherInvitation[] }>(
      `/api/v1/sessions/${enc(token)}/voucher-invitations`,
    ),

  resendVoucherInvitation: (token: string, invitationId: string) =>
    // `voucher_link` is the new link: after a resend, the link in the first
    // email stops working. Optional until every environment returns it.
    request<{ invitation_id: string; last_resent_at: string; voucher_link?: string }>(
      `/api/v1/sessions/${enc(token)}/voucher-invitations/${enc(invitationId)}/resend`,
      { method: "POST", body: JSON.stringify({}) },
    ),

  revokeVoucherInvitation: (token: string, invitationId: string) =>
    request<{ invitation_id: string; status: "revoked" }>(
      `/api/v1/sessions/${enc(token)}/voucher-invitations/${enc(invitationId)}`,
      { method: "DELETE" },
    ),

  // Voucher outcome — voucher confirms, declines, or (Q1 "No") reports they
  // can't confirm the applicant. Backend automatically rescores on confirm.
  // `unable_to_confirm` + `reason` are first-class on the backend (accepted at
  // /vouch-outcome; reason stored on the invitation). applicant_score/_zone are
  // only returned on the confirm path (decline/unable_to_confirm write no
  // positive signal), so they're optional.
  submitVouchOutcome: (
    token: string,
    payload: {
      outcome: "confirm" | "decline" | "unable_to_confirm";
      reason?: string;
      notes?: string;
      qa_responses?: Record<string, string>;
    },
  ) =>
    request<VouchOutcomeResponse>(`/api/v1/sessions/${enc(token)}/vouch-outcome`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  // Plaid Income + Identity — frontend opens Plaid Link with the link_token
  // returned here, then posts the resulting public_token + metadata to
  // /exchange. Backend persists an encrypted access_token, synchronously
  // fetches Payroll Income + Identity, and writes signals. PR2 will add
  // async Bank Income / Document Income via webhooks.
  plaidLinkToken: (token: string) =>
    request<{ link_token: string; expiration: string }>(
      `/api/v1/verify/plaid/link-token`,
      { method: "POST", body: JSON.stringify({ session_token: token }) },
    ),

  // Dedicated Payroll Income link token. Payroll digital income runs in its
  // OWN Plaid Link session (cannot share with Identity/Bank) and is entirely
  // user_token + webhook driven — there is NO public_token / plaidExchange
  // step. The backend persists the user record at link-token time and writes
  // employment.verification when Plaid's INCOME webhook fires.
  // Returns 404 when PLAID_INCOME_ENABLED is off for the env.
  /**
   * Dedicated payroll-income Link session.
   *
   * `mode` picks the flow: "digital" connects a payroll provider (confidence
   * 0.95), "document" opens Plaid's hosted pay-stub upload module (0.70).
   * One flow per session — the backend records which one so the async INCOME
   * webhook attributes the right confidence tier. mode="document" answers 404
   * DOCUMENT_INCOME_DISABLED when the backend flag is off.
   */
  plaidPayrollLinkToken: (token: string, mode: "digital" | "document" = "digital") =>
    request<{ link_token: string; expiration: string }>(
      `/api/v1/verify/plaid/payroll/link-token`,
      { method: "POST", body: JSON.stringify({ session_token: token, mode }) },
    ),

  plaidExchange: (
    token: string,
    payload: { public_token: string; metadata?: Record<string, unknown> },
  ) =>
    request<{
      item_id: string;
      signals_written: Array<{ signal_type: string; confidence: number; source: string }>;
    }>(`/api/v1/verify/plaid/exchange`, {
      method: "POST",
      body: JSON.stringify({ session_token: token, ...payload }),
    }),

  // Plaid IDV (Identity Verification) — Phase 2 of the Plaid program.
  // See BACKEND_INTEGRATION.md §24.
  //
  // /link-token returns 503 when the env hasn't enabled PLAID_IDV_ENABLED
  // (callers should render an "IDV not available" / skip-only state).
  // /result is called from PlaidIDVButton's onSuccess; backend is STEPS-AWARE
  // (2026-06-18): it writes identity.data_source_verified when Plaid's
  // KYC/data-source check passed (the path the prod template runs first), or
  // identity.gov_id_verified + identity.selfie_match when the document path
  // ran. It returns data_source_passed / documentary_passed so the FE can
  // release the gate on EITHER path. See BACKEND_INTEGRATION.md §24.
  plaidIdvLinkToken: (token: string) =>
    request<{ link_token: string; expiration: string }>(
      `/api/v1/verify/plaid-idv/link-token`,
      { method: "POST", body: JSON.stringify({ session_token: token }) },
    ),

  // idv_session_id is OPTIONAL: Plaid Link's onSuccess metadata doesn't carry
  // the identity_verification id, so the FE normally omits it and the backend
  // resolves the user's latest IDV session by client_user_id. Pass one only if
  // a caller actually has it.
  plaidIdvResult: (token: string, idv_session_id?: string) =>
    request<{
      idv_session_id: string;
      plaid_status: string;
      documentary_status: string | null;
      selfie_status: string | null;
      kyc_status: string | null;
      // True when the KYC/data-source path passed (no document needed).
      data_source_passed: boolean;
      // True when BOTH the document + selfie steps passed.
      documentary_passed: boolean;
      signals_written: Array<{ signal_type: string; confidence: number; result?: string }>;
    }>(`/api/v1/verify/plaid-idv/result`, {
      method: "POST",
      body: JSON.stringify(
        idv_session_id ? { session_token: token, idv_session_id } : { session_token: token },
      ),
    }),

  // POST /verify/plaid-idv/skip — graceful exit when the voucher
  // exhausted Plaid IDV retries. Flips session.voucher_idv_skipped=true
  // server-side so the vouch-outcome gate accepts the submission.
  // No signals written; operator sees the skip flag on the case.
  plaidIdvSkip: (token: string) =>
    request<{ session_id: string; voucher_idv_skipped: true }>(
      `/api/v1/verify/plaid-idv/skip`,
      { method: "POST", body: JSON.stringify({ session_token: token }) },
    ),
};

export interface VoucherActivateResponse {
  session_token: string;
  applicant_name?: string;
  relationship?: string;
}

/**
 * Voucher-recipient API — uses the invitation token (not a session token).
 * Activate exchanges the invitation token for a real session token that
 * can then be used with all `/api/v1/sessions/:session_token/...` endpoints.
 *
 * Errors:
 *   - 409 ACCEPTED  → invitation already used
 *   - 410           → invitation expired
 *   - 404           → invalid invitation
 */
export const voucherApi = {
  activate: (invitationToken: string) =>
    request<VoucherActivateResponse>(
      `/api/v1/voucher/${enc(invitationToken)}/activate`,
      { method: "POST", body: JSON.stringify({}) },
    ),
};

/**
 * Applicant-facing voucher invitation status, as carried by
 * `GET /api/v1/sessions/:token/voucher-invitations`.
 *
 * ⚠️ The backend deliberately COLLAPSES the raw DB status before the applicant
 * sees it — `normalizeVoucherStatusForApplicant` in digavouch-platform
 * `services/digavouch/src/api/routes/vouchers.ts` (verified as of 2026-08-10,
 * `vouchers.ts:65-77` applied at `:259`):
 *
 *     accepted → "complete"          declined → "complete"
 *     pending / revoked / expired → unchanged
 *
 * So **`"complete"` is what the wire actually carries** once a voucher has
 * responded, and `"accepted"` / `"declined"` are NEVER sent on this endpoint.
 * The asymmetry is intentional: the applicant is not told *which* voucher
 * refused. Operator-facing endpoints are not normalized and do see the raw
 * status, but they use their own response shape — not this type.
 *
 * `accepted` / `declined` stay in the union only as defensive legacy values
 * (the helper passes unknown statuses through unchanged). Any code that
 * branches on status MUST handle `"complete"`, or completed vouches silently
 * vanish from the applicant's list — the bug Patrick hit in prod on
 * 2026-08-06, where a confirmed vouch disappeared and re-inviting the same
 * address 409'd as a duplicate.
 */
export type VoucherInvitationStatus =
  | "pending"
  | "complete"
  | "expired"
  | "revoked"
  /** @deprecated Not emitted by the applicant endpoint — see the note above. */
  | "accepted"
  /** @deprecated Not emitted by the applicant endpoint — see the note above. */
  | "declined";

export interface VoucherInvitation {
  invitation_id: string;
  name: string;
  email: string;
  relationship: string;
  status: VoucherInvitationStatus;
  sent_at: string;
  expires_at: string;
  last_resent_at?: string;
  /**
   * Always null from the list endpoint. Present only when this page received
   * it from the invite or resend response.
   */
  voucher_link: string | null;
}

export const apiBaseConfigured = () => Boolean(API_BASE);

// ============================================================
// Reviewer-side API (Dashboard, Case Detail, Case Review)
// ============================================================

/**
 * Backend zones (from the scoring engine):
 *   - "clear" (score >= 0.70) → recommend approve
 *   - "gray"  (0.36-0.69)     → needs operator review
 *   - "flag"  (<= 0.35)       → recommend deny
 *   - "pending"               → not yet scored (null from backend)
 */
export type CaseZone = "clear" | "gray" | "flag" | "pending";

/** Backend recommendation enum values from the scoring engine. */
export type CaseRecommendation = "approve" | "request_more_proof" | "deny" | "pending";

export interface CaseSummary {
  /** Stable id — always present. Use this as the URL/route key. */
  inquiry_id: string;
  /** May be null for inquiries that lost their session. Prefer inquiry_id for routing. */
  session_id: string | null;
  /** Magic-link token for /verify/:token (when backend exposes it). */
  token?: string;
  applicant_name: string;
  applicant_email?: string;
  property?: string;
  status: SessionStatusName;
  status_label: string;
  current_step: number;
  percent_complete: number;
  zone: CaseZone;
  zone_label: string;
  /** Raw backend recommendation enum (or "pending"). */
  recommendation: CaseRecommendation;
  /** Human-readable recommendation label, ready for display. */
  recommendation_label: string;
  /** What the operator actually decided on the applicant review page — distinct from `recommendation` (system suggestion). */
  operator_action?: "approved" | "denied" | "requested_more_info" | "escalated" | null;
  signals_verified: string[];
  signals_verified_count: number;
  missing_evidence: string[];
  created_at?: string;
  updated_at?: string;
  /** ISO timestamp the case was declined (by applicant) or removed (by operator). */
  declined_at?: string;
  /** Free-text reason supplied at decline time (applicant) or removal time (operator). Max 500 chars. */
  decline_reason?: string;
  /** "applicant" → applicant opted out via /decline; "operator" → operator removed via case review. */
  declined_by?: "applicant" | "operator" | null;
  /**
   * BF-B5 / ADR-012 R3.4 — the applicant's ID-verification state, served by the
   * queue since platform#226. Narrowed at the boundary by
   * `parseApplicantIdvState`; null when the backend omits it or sends something
   * unrecognised. Not in `dv-api-contract` v0.1.3 yet — retire this local field
   * when the contract carries it.
   */
  applicant_idv_state?: ApplicantIdvState | null;
  /**
   * platform#595 — a required ID check that went unmet, and whether the case is
   * marked Review Required for it. Parsed by `parseRequiredIdv`; null when the
   * backend omits it. Not in `dv-api-contract` yet.
   */
  required_idv?: RequiredIdv | null;
  /** True when this session is a voucher invitation (not an applicant). */
  is_voucher?: boolean;
  vouching_for?: { applicant_name: string; relationship: string } | null;
  /**
   * Reason the operator selected when creating the session (e.g.
   * "thin_credit_file"). Stored as the canonical FOLLOW_UP_REASONS value
   * when available; may be a legacy free-text label for older sessions.
   */
  follow_up_reason?: string | null;
}

interface BackendCaseListItem {
  inquiry_id?: string;
  session_id: string | null;
  token?: string;
  applicant_name?: string;
  applicant_email?: string;
  email?: string;
  property?: string;
  inquiry_details?: {
    metadata?: { name?: string; email?: string; property?: string; reason?: string } | null;
    reason?: string;
  } | null;
  status: string;
  current_step?: number;
  percent_complete?: number;
  zone?: string | null;
  recommendation?: string | null;
  operator_action?: string | null;
  signals_verified?: string[];
  missing_evidence?: string[];
  /**
   * BF-B5 — typed as `unknown` rather than the enum on purpose: it is not in
   * dv-api-contract yet, so nothing upstream validates it and
   * `parseApplicantIdvState` is the only real guard. Declaring the union here
   * would assert a shape this layer cannot actually vouch for.
   */
  applicant_idv_state?: unknown;
  required_idv?: unknown;
  created_at?: string;
  updated_at?: string;
  last_updated_at?: string;
  modified_at?: string;
  is_voucher?: boolean;
  vouching_for?: { applicant_name: string; relationship: string } | null;
  declined_at?: string;
  decline_reason?: string;
  declined_by?: string | null;
  removed_at?: string;
  removal_reason?: string;
  follow_up_reason?: string | null;
  reason?: string | null;
}

const ZONE_LABEL: Record<CaseZone, string> = {
  clear: "Strong Evidence",
  gray: "Needs Review",
  flag: "Weak Signals",
  pending: "Pending",
};

const RECOMMENDATION_LABEL: Record<CaseRecommendation, string> = {
  approve: "Approve",
  request_more_proof: "Review",
  deny: "Deny",
  pending: "Pending",
};

function normalizeRecommendation(raw: string | null | undefined): CaseRecommendation {
  if (raw === "approve" || raw === "request_more_proof" || raw === "deny") return raw;
  return "pending";
}

// Operator-friendly fallback labels for raw backend statuses.
// In practice Dashboard / Applicant render `getCaseStatus(c).pill` from
// `src/lib/caseStatus.ts`; these strings are a safety net for any caller
// that still reads `status_label`. Keep them aligned with the 8-tab set
// (no "Step-Up Required" / "Awaiting Consent" / "Declined" leakage).
const STATUS_LABEL: Record<SessionStatusName, string> = {
  pending_consent: "New",
  in_progress: "In Progress",
  step_up_required: "Waiting on Applicant",
  awaiting_vouches: "Awaiting vouches",
  completed: "Needs Review",
  expired: "Closed",
  declined: "Closed",
};

function normalizeZone(raw: string | null | undefined): CaseZone {
  if (raw === "clear" || raw === "gray" || raw === "flag") return raw;
  return "pending";
}

// Exported for the A-3 guardrail tests (src/lib/__tests__/normalizeCase.test.ts);
// production callers stay within this module.
export function normalizeCase(raw: BackendCaseListItem): CaseSummary {
  const meta = raw.inquiry_details?.metadata ?? {};
  const status = STATUS_MAP[raw.status] ?? "in_progress";
  const zone = normalizeZone(raw.zone);
  const recommendation = normalizeRecommendation(raw.recommendation);
  // Fallback so legacy payloads (or mock data) without inquiry_id still route.
  const inquiry_id = raw.inquiry_id ?? raw.session_id ?? "";
  return {
    inquiry_id,
    session_id: raw.session_id,
    token: raw.token,
    applicant_name: raw.applicant_name ?? meta.name ?? "Unknown applicant",
    applicant_email: raw.applicant_email ?? raw.email ?? meta.email,
    property: raw.property ?? meta.property,
    status,
    status_label: STATUS_LABEL[status],
    current_step: raw.current_step ?? 1,
    percent_complete: typeof raw.percent_complete === "number" ? raw.percent_complete : 0,
    zone,
    zone_label: ZONE_LABEL[zone],
    recommendation,
    recommendation_label: RECOMMENDATION_LABEL[recommendation],
    operator_action: (raw.operator_action as CaseSummary["operator_action"]) ?? null,
    signals_verified: raw.signals_verified ?? [],
    signals_verified_count: raw.signals_verified?.length ?? 0,
    missing_evidence: raw.missing_evidence ?? [],
    applicant_idv_state: parseApplicantIdvState(raw.applicant_idv_state),
    required_idv: parseRequiredIdv(raw.required_idv),
    created_at: raw.created_at,
    updated_at: raw.updated_at ?? raw.last_updated_at ?? raw.modified_at,
    declined_at: raw.declined_at ?? raw.removed_at,
    decline_reason: raw.decline_reason ?? raw.removal_reason,
    // Only trust an explicit backend actor signal for declined_by. Do NOT
    // synthesize operator closures from `operator_action === "denied"` — that
    // value means the completed case outcome was "Not Verified", not that the
    // case lifecycle was closed. Backfilling it here hides denied/completed
    // cases from the Not Verified tab by making them look Closed upstream.
    declined_by:
      raw.declined_by === "applicant" || raw.declined_by === "operator"
        ? raw.declined_by
        : status === "declined"
          ? "applicant"
          : null,
    is_voucher: raw.is_voucher,
    vouching_for: raw.vouching_for ?? null,
    follow_up_reason:
      raw.follow_up_reason ??
      raw.reason ??
      raw.inquiry_details?.reason ??
      raw.inquiry_details?.metadata?.reason ??
      null,
  };
}

export interface CreateSessionInput {
  applicant_name: string;
  email: string;
  /**
   * The Layer-1 locator — what this case is *about*. A unit in leasing, a role
   * in recruiting; one vertical-neutral field under two labels (see
   * `verticalCopy.ts`). Free text, optional.
   *
   * Named for the column it lands in (`inquiries.applied_for`) rather than for
   * the leasing label it used to carry. **Safe to rename front-end-only:** the
   * endpoint resolves the locator from `applied_for`, `property` and
   * `metadata.property` to one value, so no synchronised deploy is needed
   * (verified as of 2026-08-28, `routes/sessions.ts` `locatorCandidates`).
   * `POST /api/v1/sessions` reads its body as a hand-written cast rather than
   * a contract schema, so an undeclared key is not stripped — which is why
   * this works even though `applied_for` is in no `dv-api-contract` version.
   */
  applied_for?: string;
  /** Applicant date of birth as entered by the operator (string, format MM/DD/YYYY). Optional. */
  dob?: string;
  reason?: string;
  /**
   * Which verification journey this applicant walks (ADR-014). Omit for the
   * default flow, which is what every case created before the picker existed
   * got and what an org with no definitions still gets.
   *
   * ⚠️ **VALIDATED server-side, unlike the locator above.** An unknown key is a
   * 400 (`UNKNOWN_WORKFLOW_KEY`), never a silent fallback — the whole point is
   * that a case which looks right in the queue cannot hide an applicant walking
   * the wrong flow. So only send a key that came from `OperatorMe.org.workflows`.
   * The literal `'standard'` is rejected too: it is the absence of a flow, not
   * a choice.
   */
  workflow_key?: string;
}

export interface CreateSessionResult {
  session_id: string;
  /** The Case. Returned by the endpoint since it existed (verified as of 2026-10-13, `routes/sessions.ts` 201 body). */
  inquiry_id?: string;
  token: string;
  magic_link: string;
  /** ISO timestamp for when the magic link expires. Optional — backend may omit. */
  expires_at?: string;
}

interface BackendCreateSession {
  session_id: string;
  inquiry_id?: string;
  token?: string;
  session_token?: string;
  magic_link?: string;
  verify_url?: string;
  expires_at?: string;
}

export const reviewerApi = {
  listSessions: async (filters?: {
    status?: string;
    limit?: number;
    includeClosed?: boolean;
  }): Promise<CaseSummary[]> => {
    const qs = new URLSearchParams();
    if (filters?.status) qs.set("status", filters.status);
    if (filters?.limit) qs.set("limit", String(filters.limit));
    // Closed (declined / future-expired) cases are hidden by default — pass
    // include_closed=true to surface them. Ignored if `status` is set (the
    // explicit filter overrides).
    if (filters?.includeClosed) qs.set("include_closed", "true");
    const suffix = qs.toString() ? `?${qs}` : "";
    const raw = await request<BackendCaseListItem[] | { sessions: BackendCaseListItem[] }>(
      `/api/v1/sessions${suffix}`,
    );
    const list = Array.isArray(raw) ? raw : raw?.sessions ?? [];
    return list.map(normalizeCase);
  },

  createSession: async (input: CreateSessionInput): Promise<CreateSessionResult> => {
    const raw = await request<BackendCreateSession>(`/api/v1/sessions`, {
      method: "POST",
      body: JSON.stringify({
        applicant_name: input.applicant_name,
        email: input.email,
        applied_for: input.applied_for,
        // `dob` is sent verbatim as a string. Backend field name pending
        // confirmation — see docs/backend-brief-add-applicant-fields.md.
        dob: input.dob,
        reason: input.reason,
        // Omitted entirely when unset, rather than sent as null/"" — the
        // endpoint treats absent as "default journey", and there is no reason
        // to make a request say something it does not mean.
        ...(input.workflow_key ? { workflow_key: input.workflow_key } : {}),
      }),
    });

    const token = raw.token ?? raw.session_token ?? "";
    const magic_link =
      raw.magic_link ??
      raw.verify_url ??
      (token ? `${window.location.origin}/verify/${token}` : "");
    return {
      session_id: raw.session_id,
      inquiry_id: raw.inquiry_id,
      token,
      magic_link,
      expires_at: raw.expires_at,
    };
  },

  getCaseReview: async (inquiryId: string): Promise<CaseReview> => {
    return request<CaseReview>(`/api/v1/cases/${enc(inquiryId)}/review`);
  },

  /**
   * GET /api/v1/verification-dashboard-summary — see BACKEND_INTEGRATION.md §18.
   * Org-scoped, 30s server cache. Throws `ApiError` with `status: 404` on
   * older backend deploys; callers should fall back to FE `computeSnapshot`.
   */
  getDashboardSummary: async (): Promise<DashboardSummary> => {
    return request<DashboardSummary>(`/api/v1/verification-dashboard-summary`);
  },

  getInquiryResult: async (inquiryId: string): Promise<InquiryResult> => {
    return request<InquiryResult>(`/api/v1/inquiries/${enc(inquiryId)}/result`);
  },

  saveReviewerNote: async (
    inquiryId: string,
    note: string,
  ): Promise<{ inquiry_id: string; reviewer_note: string }> => {
    return request<{ inquiry_id: string; reviewer_note: string }>(
      `/api/v1/cases/${enc(inquiryId)}/review-note`,
      { method: "PATCH", body: JSON.stringify({ note }) },
    );
  },

  submitDecision: async (
    inquiryId: string,
    body: { decision: DecisionInput; note?: string; reason?: string; reason_label?: string },
  ): Promise<DecisionResult> => {
    try {
      return await request<DecisionResult>(`/api/v1/cases/${enc(inquiryId)}/decision`, {
        method: "POST",
        body: JSON.stringify(body),
      });
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 409 || err.code === "ALREADY_DECIDED") {
        const ad = Object.assign(new Error("Inquiry is already decided"), {
          status: 409 as const,
          code: "ALREADY_DECIDED" as const,
          alreadyDecided: true as const,
        }) as AlreadyDecidedError;
        throw ad;
      }
      throw e;
    }
  },

  /**
   * Soft-close (archive) a case. Sends a POST to the backend's close endpoint
   * so the case stays in the database with status="declined" + declined_by=
   * "operator" and shows up under the Closed filter. See
   * docs/closed-cases-backend-gap.md for the contract expected from the API.
   */
  closeCase: async (
    inquiryId: string,
    reason?: string,
  ): Promise<{ inquiry_id: string; declined_at: string }> => {
    return request<{ inquiry_id: string; declined_at: string }>(
      `/api/v1/cases/${enc(inquiryId)}/close`,
      {
        method: "POST",
        body: JSON.stringify({ reason: reason ?? null }),
      },
    );
  },

  /**
   * Reopen a closed (declined / archived) case. Backend reactivates the
   * verification request, expires any stale sessions, cancels in-flight
   * vouches (with cancellation_reason='case_reopened'), creates a new
   * session, and — in UAT/production — emails the applicant a new
   * verification link. In staging the backend suppresses the email send.
   *
   * Only operator-closed cases are reopenable (lock §7.2 in the design
   * doc). Applicant-declined cases return 409 CANNOT_REOPEN_APPLICANT_DECLINED.
   * 1/lifetime rate limit; second attempt returns 409 ALREADY_REOPENED.
   *
   * See docs/reopen-closed-case.md and the backend design doc
   * docs/design/operator-next-step-actions.md §3.6.
   */
  reopenCase: async (
    inquiryId: string,
    reason?: string,
  ): Promise<{
    inquiry_id: string;
    session_token: string;
    verify_url: string;
    expires_at: string;
    reopened_at: string;
    cancelled_vouchers: number;
    idempotent_replay: boolean;
  }> => {
    return request<{
      inquiry_id: string;
      session_token: string;
      verify_url: string;
      expires_at: string;
      reopened_at: string;
      cancelled_vouchers: number;
      idempotent_replay: boolean;
    }>(`/api/v1/cases/${enc(inquiryId)}/reopen`, {
      method: "POST",
      body: JSON.stringify({ reason: reason ?? null }),
    });
  },

  // ---------- Cat 4 iv: operator next-step actions ----------
  // Each maps 1:1 to a backend endpoint in
  // services/digavouch/src/api/routes/results.ts. Errors surface via the
  // shared ApiError shape — callers can inspect err.code for:
  //   - RATE_LIMITED (429) — err.payload has retry_after_seconds (number)
  //     and window_hours (number | null). Lifetime limits return null hours.
  //   - INQUIRY_TERMINAL (409) — case is closed/declined/decided
  //   - NO_APPLICANT_EMAIL (422) — no email on the inquiry
  //   - ALREADY_REOPENED (409, reopen only) — 1/lifetime limit exhausted
  //   - CANNOT_REOPEN_APPLICANT_DECLINED (409, reopen only)

  /** iv-3: re-send the verification magic link. Reuses existing session if
   * non-expired; mints a fresh one otherwise. Rate limit 3/24h per case. */
  resendLink: async (
    inquiryId: string,
  ): Promise<{
    inquiry_id: string;
    session_token: string;
    verify_url: string;
    expires_at: string;
    sent_at: string;
    idempotent_replay: boolean;
  }> => {
    return request(`/api/v1/cases/${enc(inquiryId)}/resend-link`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },

  /** iv-4: nudge an applicant who started but hasn't finished. Optional
   * 0-280 char operator message rendered as a tinted callout in the email.
   * Rate limit 2/24h per case. */
  sendReminder: async (
    inquiryId: string,
    message?: string,
  ): Promise<{
    inquiry_id: string;
    session_token: string;
    verify_url: string;
    expires_at: string;
    sent_at: string;
    message_included: boolean;
    idempotent_replay: boolean;
  }> => {
    return request(`/api/v1/cases/${enc(inquiryId)}/send-reminder`, {
      method: "POST",
      body: JSON.stringify({ message: message ?? null }),
    });
  },

  /** iv-5: operator asks for additional info. Sets follow_up_reason +
   * operator_action='requested_more_info' on the inquiry (status untouched
   * so the case stays active for iteration). Sends a contextual
   * "more info needed" email by default — pass send_email=false to
   * record-only (e.g. when pairing with reopenCase). Rate limit 5/lifetime. */
  requestMoreInfo: async (
    inquiryId: string,
    opts: { reason: string; send_email?: boolean },
  ): Promise<{
    inquiry_id: string;
    follow_up_reason: string;
    operator_action: "requested_more_info";
    email_sent: boolean;
    idempotent_replay: boolean;
  }> => {
    return request(`/api/v1/cases/${enc(inquiryId)}/request-more-info`, {
      method: "POST",
      body: JSON.stringify({
        reason: opts.reason,
        // Only include send_email when explicitly false — backend defaults
        // to true so omitting the field keeps the email-on path.
        ...(opts.send_email === false ? { send_email: false } : {}),
      }),
    });
  },

  /** iv-2: append a timestamped operator-internal note. Body required,
   * 1-2000 chars (trimmed + capped by backend). No rate limit. */
  addNote: async (
    inquiryId: string,
    body: string,
  ): Promise<{
    inquiry_id: string;
    note_id: string;
    created_at: string;
    body: string;
    idempotent_replay: boolean;
  }> => {
    return request(`/api/v1/cases/${enc(inquiryId)}/notes`, {
      method: "POST",
      body: JSON.stringify({ body }),
    });
  },

  /** platform#595: offer the candidate a required ID check again. Reopens their
   * Run at the check and emails a new link. Never changes the score or the
   * decision. 409 REQUIRED_IDV_NOT_UNDER_REVIEW unless the case is Review
   * Required for this reason; 429 after three retries on one case. */
  retryRequiredIdv: async (
    inquiryId: string,
  ): Promise<{ inquiry_id: string; email_sent: boolean; expires_at: string; idempotent_replay: boolean }> => {
    return request(`/api/v1/cases/${enc(inquiryId)}/required-idv/retry`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },

  /** platform#595: record a manual review of a required ID check that went
   * unmet, with the operator's reason (1-2000 chars). Does not clear Review
   * Required; the decision stays the existing Proceed / Not Verified. */
  recordRequiredIdvManualReview: async (
    inquiryId: string,
    reason: string,
  ): Promise<{ inquiry_id: string; note_id: string; created_at: string; reason: string }> => {
    return request(`/api/v1/cases/${enc(inquiryId)}/required-idv/manual-review`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },

  /** iv-2: list notes chronologically for the applicant page notes panel. */
  listNotes: async (
    inquiryId: string,
  ): Promise<{
    inquiry_id: string;
    notes: Array<{
      note_id: string;
      body: string;
      author_actor: string;
      created_at: string;
    }>;
  }> => {
    return request(`/api/v1/cases/${enc(inquiryId)}/notes`, { method: "GET" });
  },
};

// ---------- Case Review types ----------

export type DecisionInput = "proceed" | "hold" | "request_more_info";

export interface DecisionResult {
  inquiry_id: string;
  decision: string;
  action: string;
  decided_at: string;
}

export interface AlreadyDecidedError extends Error {
  status: 409;
  code: "ALREADY_DECIDED";
  alreadyDecided: true;
}

// GET /cases/:inquiry_id/review wire shapes, owned by the contract.
export type CaseReviewSource = ContractCaseReviewSource;

export type CaseReviewEvidenceItem = ContractCaseReviewEvidenceItem;

export type CaseReviewTimelineEvent = ContractCaseReviewTimelineEvent;

export type CaseReviewContinuityItem = ContractCaseReviewContinuityItem;

export type CaseReviewPillar = ContractCaseReviewPillar;

export type CaseReview = CaseReviewResponse;

// ---------- Inquiry Result (operator-facing rich detail view) ----------

export interface InquiryResultSignal {
  type: string;
  status: "verified" | "pending";
  confidence: number | null;
  detail: string;
  verified_at: string | null;
}

export interface InquiryResultVoucher {
  invitation_id: string;
  voucher_name: string;
  voucher_email: string;
  relationship: string;
  status: string;
  outcome: string | null;
  outcome_notes: string | null;
  qa_responses: Record<string, unknown> | null;
  outcome_recorded_at: string | null;
  sent_at: string;
  expires_at: string;
}

// ζ — Activity events stream (see digavouch-platform/docs/design/inquiry-result-events.md).
// Backend ships these on /api/v1/inquiries/:id/result alongside the existing
// top-level fields. Older backends omit `events` entirely; consumers should
// treat it as optional.
export type ActivityEventKind =
  // Lifecycle
  | "verification_link_sent"
  | "verification_link_resent"
  | "applicant_started"
  | "consent_granted"
  | "verification_submitted"
  | "result_decided"
  | "case_closed"
  // Signal verification
  | "email_verified"
  | "social_account_verified"
  | "email_history_verified"
  | "phone_verified"
  | "identity_verified"
  | "income_verified"
  // Vouching
  | "vouch_invited"
  | "vouch_responded"
  // Operator next-step actions (Cat 4 iv)
  | "operator_requested_more_info"
  | "operator_sent_reminder"
  | "operator_added_note"
  | "operator_case_reopened";

export interface ActivityEvent {
  kind: ActivityEventKind;
  /** ISO-8601. */
  at: string;
  /** Human-readable detail rendered as a subtitle on the timeline row. */
  detail?: string | null;
  actor?: "applicant" | "operator" | "voucher" | "system" | null;
  source_id?: string | null;
}

export interface InquiryResult {
  inquiry_id: string;
  applicant: { name: string; email_domain: string | null };
  score: number | null;
  zone: string | null;
  recommendation: string | null;
  signals_summary: InquiryResultSignal[];
  contributing_factors: Array<{ signal_type: string; weight: number; contribution: number }>;
  verification_progress: { steps_completed: string[]; steps_total: number; percent_complete: number };
  submitted_at: string | null;
  decided_at: string | null;
  operator_action: string | null;
  vouchers: InquiryResultVoucher[];
  /** Chronological activity stream (ζ v1). May be absent on older backends. */
  events?: ActivityEvent[];
}

/**
 * Org-scoped dashboard summary returned by GET /api/v1/verification-dashboard-summary.
 * See BACKEND_INTEGRATION.md §18.1. Rates may be `null` when the denominator
 * is zero. `completion_rate_percent` is the legacy alias of the outcome-rate
 * metric — kept for compatibility but the FE prefers explicit fields.
 */
export interface DashboardSummary {
  needs_review_count: number;
  needs_attention_count: number;
  waiting_on_applicant_count: number;
  /** Sub-count of waiting_on_applicant — applicant still owes submission. */
  needs_to_submit_count?: number;
  /** Sub-count of waiting_on_applicant — applicant submitted, vouch pending. */
  awaiting_vouch_count?: number;
  dropped_off_count: number;
  verified_count: number;
  not_verified_count: number;
  avg_time_to_complete_days: number | null;
  completion_rate_percent: number | null;
  verified_rate_percent: number | null;
  window?: { avg_time_window_days?: number; completion_rate_window_days?: number | null };
}




// ---------- Admin API (admin.digavouch.com /tenants) ----------
// Authenticated with a Google ID token (Authorization: Bearer) verified by the
// admin-auth middleware + email allowlist — distinct from the operator
// dashboard's Supabase access token (also Bearer, but verified via Supabase →
// operator_memberships). See services/digavouch admin-auth middleware.

export interface AdminOrgSummary {
  org_id: string;
  name: string;
  subdomain: string | null;
  status: string;
  industry: string;
  primary_contact: { name: string | null; email: string } | null;
  notification_recipients: { email: string }[] | null;
  created_at: string;
  active_key_prefixes: string[];
  /** Operator emails with dashboard access (operator_memberships). */
  operator_emails: string[];
}

export interface CreateOrgInput {
  name: string;
  subdomain?: string | null;
  contactEmails?: string[];
  /** Emails granted operator-dashboard access (seeded into operator_memberships). */
  operatorEmails?: string[];
  primaryContactName?: string;
  status?: string;
  /**
   * The org's vertical, one of `KNOWN_VERTICALS` (`lib/verticalCopy.ts`).
   * Required here even where the API still fills in a missing one as
   * `residential_leasing` (verified as of 2026-10-14): the admin form must
   * choose it either way.
   */
  industry: string;
}

export interface CreateOrgResult {
  org_id: string;
  name: string;
  subdomain: string | null;
  primary_contact: { name: string | null; email: string };
  contact_emails: string[];
  operator_emails: string[];
  scoring_rules_count: number;
  api_key: string; // shown ONCE
  key_prefix: string;
  /**
   * Operator emails granted membership here that already resolve, via the
   * backend's earliest-membership rule, to a DIFFERENT org — that org is
   * where the operator will keep landing (platform#601 / PR #682).
   */
  operator_warnings?: Array<{ email: string; shadowed_by: { org_id: string; org_name: string } }>;
}

export interface UpdateOrgInput {
  name?: string;
  status?: string;
  subdomain?: string | null;
  contactEmails?: string[];
  primaryContactName?: string;
}

async function adminRequest<T>(path: string, idToken: string, init: RequestInit = {}): Promise<T> {
  if (!API_BASE) {
    const err: ApiError = new Error("VITE_API_BASE_URL is not configured.");
    err.status = 0;
    throw err;
  }
  const hasBody = init.body != null;
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(hasBody ? { "Content-Type": "application/json" } : {}),
      Accept: "application/json",
      Authorization: `Bearer ${idToken}`,
      ...(init.headers ?? {}),
    },
  });
  const text = await res.text();
  const payload = text ? safeJson(text) : null;
  if (!res.ok) {
    const p = payload as { message?: string; error?: string; code?: string } | null;
    const err: ApiError = new Error(p?.message ?? p?.error ?? `Request failed (${res.status})`);
    err.status = res.status;
    err.code = p?.code;
    err.payload = payload;
    throw err;
  }
  return payload as T;
}

// ---------- Operator self-info (operator-login routing/branding) ----------

export interface OperatorMe {
  email: string | null;
  /** The operator's org (from operator_memberships), or null when unassigned. */
  org: {
    org_id: string;
    name: string;
    subdomain: string | null;
    /**
     * The org's vertical, for OPERATOR surfaces. The public flows read
     * `org_industry` off session status instead (see SessionStatus above) —
     * an applicant has no operator membership to resolve.
     *
     * Optional because absent means *unknown*, not any particular value:
     * a consumer pinned against an older server gets nothing here, and
     * `verticalFrom()` falls back rather than guessing.
     */
    industry?: string;

    /**
     * Non-'standard' workflow keys this org has definitions for. A statement
     * about configuration, not a product category — `operatorHome()` decides
     * what it means for routing.
     *
     * OPTIONAL because a consumer pinned to an older server keeps parsing:
     * absent means unknown, not empty. Declared from dv-api-contract v0.1.6
     * (there is no v0.1.5 tag) and live in all three environments since the
     * 2026-08-28 platform deploy.
     */
    workflow_keys?: string[];

    /**
     * The same set as `workflow_keys`, carrying the name to render beside each.
     *
     * A `workflow_key` is an ADDRESS — frozen, part of the definition table's
     * unique constraint and persisted onto the session — so a dropdown of raw
     * keys is not shippable. This is what the flow picker renders.
     *
     * ⚠️ `display_name` is usually **null**. The column was added without a
     * backfill because what these flows should be *called* is an open product
     * question, and the server passes null through rather than defaulting it.
     * Null means fall back to the key — see `workflowLabel()`.
     *
     * OPTIONAL because a consumer pinned to an older server keeps parsing.
     * Declared from dv-api-contract v0.1.7.
     */
    workflows?: { workflow_key: string; display_name: string | null }[];
  } | null;

  /**
   * Platform-staff status (the staff org switch; platform PR
   * `feat/juan/staff-org-switch`, verified as of 2026-10-08). `null` or absent
   * for everyone who is not DigaVouch staff, which is every customer operator.
   *
   * When present, `org` above is the org the request ACTED IN: the selected
   * org while switched, the home org otherwise. So the vertical, the subdomain
   * redirect and the flow picker follow the selection without reading this.
   */
  staff?: OperatorMeStaff | null;
}

export interface OperatorMeSwitchableOrg {
  org_id: string;
  name: string;
  subdomain: string | null;
  industry: string;
  status: string;
  /** The person's own (home) org, when it is in scope. */
  is_home: boolean;
}

export interface OperatorMeStaff {
  /** The person's own membership org; null for staff with no membership. */
  home_org_id: string | null;
  /** The org being viewed when switched; null at home. */
  selected_org_id: string | null;
  switched: boolean;
  /** `any_org` on staging and UAT; `allowed_orgs_only` in production. */
  scope: "any_org" | "allowed_orgs_only";
  /** What the switcher may offer, by name. */
  switchable_orgs: OperatorMeSwitchableOrg[];
}

export interface OrgSwitchResult {
  org: { org_id: string; name: string; subdomain: string | null; industry: string };
  switched: boolean;
  home_org_id: string | null;
  /** What to send as `X-DigaVouch-Org` from now on: the org id when switched, null at home. */
  selection: string | null;
}

export const operatorApi = {
  // Uses request() → Authorization: Bearer (the logged-in operator's Supabase
  // token). 200 { org: null } means logged-in-but-unassigned (route to app).
  me: () => request<OperatorMe>(`/api/v1/operator/me`),
  /**
   * Staff only: enter an org (`null` = back to the home org). The backend
   * validates the target against the environment's scope and RECORDS the
   * switch; the selection then travels on every call as `X-DigaVouch-Org`
   * (see `staffOrgSelection.ts`). 403 `STAFF_ONLY` for anyone else, 403
   * `ORG_SELECTION_FORBIDDEN` for an org out of scope.
   */
  switchOrg: (orgId: string | null) =>
    request<OrgSwitchResult>(`/api/v1/operator/org-switch`, {
      method: "POST",
      body: JSON.stringify({ org_id: orgId }),
    }),
};

// ---------- Admin inquiry timeline (R1 — backend platform PR #144) ----------

export interface AdminInquirySummary {
  inquiry_id: string;
  org_id: string;
  org_name: string;
  applicant_name: string | null;
  applicant_email: string | null;
  status: string;
  current_score: string | null;
  current_zone: string | null;
  recommendation: string | null;
  operator_action: string | null;
  created_at: string;
  updated_at: string;
  voucher_invites: number;
  vouches_completed: number;
}

export interface AdminInquiryHeader extends Omit<AdminInquirySummary, "voucher_invites" | "vouches_completed"> {
  person_id: string;
  applicant_phone: string | null;
  operator_action_by: string | null;
  operator_action_at: string | null;
  declined_at: string | null;
  decline_reason: string | null;
  deleted_at: string | null;
  follow_up_reason: string | null;
  not_verified_reason: string | null;
  not_verified_reason_label: string | null;
  not_verified_note: string | null;
}

export interface AdminInquiryInvitation {
  invitation_id: string;
  voucher_name: string;
  voucher_email: string;
  relationship: string;
  channel: string | null;
  status: string;
  outcome: string | null;
  outcome_recorded_at: string | null;
  outcome_notes: string | null;
  sent_at: string | null;
  last_resent_at: string | null;
  cancellation_reason: string | null;
  voucher_inquiry_id: string | null;
  voucher_person_id: string | null;
  voucher_self_score: string | null;
  voucher_self_zone: string | null;
}

export interface AdminTimelineEvent {
  at: string;
  event: string;
  severity: "error" | "warn" | null;
  detail: Record<string, unknown>;
}

export interface AdminInquiryTimeline {
  inquiry: AdminInquiryHeader;
  invitations: AdminInquiryInvitation[];
  events: AdminTimelineEvent[];
}

export const adminApi = {
  listOrgs: (idToken: string) =>
    adminRequest<{ organizations: AdminOrgSummary[] }>(`/api/v1/admin/organizations`, idToken),
  createOrg: (idToken: string, body: CreateOrgInput) =>
    adminRequest<CreateOrgResult>(`/api/v1/admin/organizations`, idToken, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateOrg: (idToken: string, orgId: string, body: UpdateOrgInput) =>
    adminRequest<AdminOrgSummary>(`/api/v1/admin/organizations/${enc(orgId)}`, idToken, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  listInquiries: (idToken: string, opts: { q?: string; days?: number } = {}) => {
    const params = new URLSearchParams();
    if (opts.q?.trim()) params.set("q", opts.q.trim());
    if (opts.days) params.set("days", String(opts.days));
    const qs = params.toString();
    return adminRequest<{ inquiries: AdminInquirySummary[] }>(
      `/api/v1/admin/inquiries${qs ? `?${qs}` : ""}`,
      idToken,
    );
  },
  inquiryTimeline: (idToken: string, inquiryId: string) =>
    adminRequest<AdminInquiryTimeline>(`/api/v1/admin/inquiries/${enc(inquiryId)}/timeline`, idToken),
};
